import type { Schema } from '@dealdesk/api-schema';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { uploadFile } from '@/shared/api';
import { attachFiles } from '@/shared/files';
import { idempotent, ifMatch } from '@/shared/api/headers';

import { COMMAND_OF, commandBody } from '../model/commands';
import { dealKeys } from './keys';
import { dealQuery, settlementQuery } from './queries';

type Deal = Schema<'Deal'>;

/** Version of the deal the user is looking at (the cached record; fetched only when absent), for If-Match on deal commands. */
async function dealVersion(qc: QueryClient, dealId: string): Promise<string> {
  return (await qc.query({ ...dealQuery(dealId), staleTime: 'static' })).version;
}

/** Version of the settlement plan on screen, for If-Match on settlement commands. */
async function planVersion(qc: QueryClient, dealId: string): Promise<string> {
  return (await qc.query({ ...settlementQuery(dealId), staleTime: 'static' })).version;
}

/** Everything that shows deal state outside the record itself. */
function invalidateDealViews(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: dealKeys.lists() });
  void qc.invalidateQueries({ queryKey: ['ops'] });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
  void qc.invalidateQueries({ queryKey: ['nav-counts'] });
  void qc.invalidateQueries({ queryKey: ['customers'] });
}

function setDeal(qc: QueryClient, deal: Deal) {
  qc.setQueryData(dealKeys.detail(deal.id), deal);
  void qc.invalidateQueries({ queryKey: dealKeys.detail(deal.id), predicate: (q) => q.queryKey.length > 3 });
  invalidateDealViews(qc);
}

export function useCreateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'DealCreate'>) => unwrap(api.POST('/deals', { body, params: { header: idempotent() } })),
    onSuccess: (deal) => setDeal(qc, deal),
  });
}

export function useUpdateDeal(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, body }: { version: string; body: Schema<'DealUpdate'> }) => unwrap(api.PATCH('/deals/{dealId}', { params: { path: { dealId }, header: { 'If-Match': version } }, body })),
    onSuccess: (deal) => {
      qc.setQueryData(dealKeys.detail(deal.id), deal);
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'overview') });
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'activity') });
      void qc.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

/**
 * Stage transitions through the explicit command endpoints (POST /deals/{dealId}/commands/{command}). Never optimistic:
 * the server decides and returns the new deal. Every command sends If-Match (the deal version on screen) and a fresh
 * Idempotency-Key per user action.
 */
export function useDealCommand(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    /**
     * `evidence`: the bank's disbursal confirmation, uploaded first and sent with confirm-disbursal (kept as a
     * DISBURSAL_CONFIRMATION deal document). `attachment`: the approval letter, attached to the new offer afterwards.
     */
    mutationFn: async ({ version, body, evidence, attachment }: { version: string; body: Schema<'TransitionRequest'>; evidence?: File | null; attachment?: File | null }) => {
      const command = COMMAND_OF[body.action];
      if (!command) throw new Error(`No command endpoint for ${body.action}`);
      const evidenceUploadId = evidence && body.action === 'disburse' ? await uploadFile(evidence, () => undefined, undefined, 'document') : undefined;
      const deal = await unwrap(
        api.POST('/deals/{dealId}/commands/' + command as '/deals/{dealId}/commands/cancel', {
          params: { path: { dealId }, header: { ...ifMatch(version), ...idempotent() } },
          body: { ...commandBody(body), ...(evidenceUploadId ? { evidenceUploadId } : {}) } as Schema<'CancelDealCommand'>,
        }),
      );
      if (attachment && body.action === 'bank_approve' && deal.currentOffer) await attachFiles('offer', deal.currentOffer.id, [attachment]);
      return deal;
    },
    onSuccess: (deal) => {
      setDeal(qc, deal);
      // Disbursal accrues commission; collections figures change.
      void qc.invalidateQueries({ queryKey: ['collections'] });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
    },
  });
}

/**
 * Finance confirms a booked deal's pending cancellation (POST /deals/{dealId}/commands/confirm-booked-cancellation,
 * collection.manage). Never optimistic; If-Match is the deal version on screen.
 */
export function useConfirmBookedCancellation(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, note }: { version: string; note?: string }) =>
      unwrap(api.POST('/deals/{dealId}/commands/confirm-booked-cancellation', { params: { path: { dealId }, header: { ...ifMatch(version), ...idempotent() } }, body: note ? { note } : {} })),
    onSuccess: (deal) => {
      setDeal(qc, deal);
      void qc.invalidateQueries({ queryKey: ['collections'] });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
    },
  });
}

/** Uploads the file (purpose document), then adds it as a new evidence version with its issue / expiry dates. */
export function useUploadDocument(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentId, file, onProgress, dates }: { documentId: string; file: File; onProgress: (p: number) => void; dates?: { issuedOn?: string; expiresOn?: string } }) => {
      const uploadId = await uploadFile(file, onProgress);
      return unwrap(api.POST('/deals/{dealId}/documents/{documentId}/files', { params: { path: { dealId, documentId } }, body: { uploadId, ...(dates ?? {}) } }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      invalidateDealViews(qc);
    },
  });
}

/** Waives an open requirement that is no longer needed (document.review; reason required; audited). */
export function useWaiveDocument(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, reason }: { documentId: string; reason: string }) => unwrap(api.POST('/deals/{dealId}/documents/{documentId}/waive', { params: { path: { dealId, documentId } }, body: { reason } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      invalidateDealViews(qc);
    },
  });
}

export function useReviewDocument(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: Schema<'DocumentReview'> }) => unwrap(api.POST('/deals/{dealId}/documents/{documentId}/review', { params: { path: { dealId, documentId } }, body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) }),
  });
}

export function useCompleteStep(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, note }: { stepId: string; note?: string }) => unwrap(api.POST('/deals/{dealId}/operations/{stepId}/complete', { params: { path: { dealId, stepId } }, body: note ? { note } : {} })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      invalidateDealViews(qc);
    },
  });
}

/** Bank updates may change the stage (query, approval, rejection, duplicate) — refresh the record and every view. */
export function useLogBankUpdate(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    /**
     * Logs the update, then attaches the supporting files to it (the bank's mail, the approval letter). A file that
     * fails to attach does not undo the update: `attachFailed` lets the caller say so (it can be attached later).
     */
    mutationFn: async ({ body, files = [] }: { body: Schema<'BankUpdateCreate'>; files?: readonly File[] }) => {
      const update = await unwrap(api.POST('/deals/{dealId}/bank-updates', { params: { path: { dealId }, header: idempotent() }, body }));
      let attachFailed = false;
      if (files.length) {
        try {
          await attachFiles('bank_update', update.id, files);
        } catch {
          attachFailed = true;
        }
      }
      return { update, attachFailed };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      invalidateDealViews(qc);
    },
  });
}

export function useResolveBankUpdate(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ updateId, body }: { updateId: string; body: Schema<'BankUpdateResolve'> }) =>
      unwrap(api.POST('/deals/{dealId}/bank-updates/{updateId}/resolve', { params: { path: { dealId, updateId }, header: idempotent() }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      invalidateDealViews(qc);
    },
  });
}

/** Records the bank's stage. Never optimistic; the response carries the deal and any suggested macro action. */
export function useRecordBankStage(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, body }: { version: string; body: Schema<'BankStageMove'> }) => unwrap(api.POST('/deals/{dealId}/bank-stage', { params: { path: { dealId }, header: { ...ifMatch(version), ...idempotent() } }, body })),
    onSuccess: (res) => setDeal(qc, res.deal),
    onError: () => void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) }),
  });
}

export function useChangeResponsibility(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schema<'ResponsibilityChange'>) =>
      unwrap(api.PUT('/deals/{dealId}/responsibility', { params: { path: { dealId }, header: { ...ifMatch(await dealVersion(qc, dealId)), ...idempotent() } }, body })),
    onSuccess: (deal) => setDeal(qc, deal),
  });
}

export function useHoldDeal(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schema<'HoldRequest'>) =>
      unwrap(api.POST('/deals/{dealId}/hold', { params: { path: { dealId }, header: { ...ifMatch(await dealVersion(qc, dealId)), ...idempotent() } }, body })),
    onSuccess: (deal) => setDeal(qc, deal),
  });
}

export function useReleaseHold(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (note?: string) =>
      unwrap(api.POST('/deals/{dealId}/hold/release', { params: { path: { dealId }, header: { ...ifMatch(await dealVersion(qc, dealId)), ...idempotent() } }, body: note ? { note } : {} })),
    onSuccess: (deal) => setDeal(qc, deal),
  });
}

export function useReplaceIncome(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, components }: { version: string; components: Schema<'IncomeComponentInput'>[] }) => unwrap(api.PUT('/deals/{dealId}/income', { params: { path: { dealId }, header: { 'If-Match': version } }, body: { components } })),
    onSuccess: (app) => {
      qc.setQueryData(dealKeys.tab(dealId, 'application'), app);
      void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId), exact: true });
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'activity') });
    },
  });
}

export function useVerifyDocument(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: Schema<'DocumentVerificationUpdate'> }) => unwrap(api.POST('/deals/{dealId}/documents/{documentId}/verification', { params: { path: { dealId, documentId } }, body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) }),
  });
}

function setPlan(qc: QueryClient, dealId: string, plan: Schema<'SettlementPlan'>) {
  qc.setQueryData(dealKeys.tab(dealId, 'settlement'), plan);
  void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId), exact: true });
  void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'overview') });
  void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'activity') });
  void qc.invalidateQueries({ queryKey: ['ops'] });
}

export function useAddSettlementItem(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schema<'SettlementItemCreate'>) =>
      unwrap(api.POST('/deals/{dealId}/settlement/items', { params: { path: { dealId }, header: { ...ifMatch(await planVersion(qc, dealId)), ...idempotent() } }, body })),
    onSuccess: (plan) => setPlan(qc, dealId, plan),
  });
}

export function useSettlementEvent(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, body }: { itemId: string; body: Schema<'SettlementEvent'> }) =>
      unwrap(api.POST('/deals/{dealId}/settlement/items/{itemId}/events', { params: { path: { dealId, itemId }, header: { ...ifMatch(await planVersion(qc, dealId)), ...idempotent() } }, body })),
    onSuccess: (plan) => setPlan(qc, dealId, plan),
  });
}

/** Saves the shortfall; a proof-of-funding file is uploaded first and sent as an upload id. */
export function useSetShortfall(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, file }: { body: Omit<Schema<'ShortfallInput'>, 'evidenceUploadId'>; file: File | null }) => {
      const evidenceUploadId = file ? await uploadFile(file, () => undefined, undefined, 'shortfall_evidence') : undefined;
      const version = await planVersion(qc, dealId);
      return unwrap(api.PUT('/deals/{dealId}/settlement/shortfall', { params: { path: { dealId }, header: ifMatch(version) }, body: { ...body, ...(evidenceUploadId ? { evidenceUploadId } : {}) } }));
    },
    onSuccess: (plan) => setPlan(qc, dealId, plan),
  });
}

export function useAddComment(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => unwrap(api.POST('/deals/{dealId}/comments', { params: { path: { dealId }, header: idempotent() }, body: { text } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'activity') });
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'overview') });
      void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId), exact: true });
    },
  });
}

const OWNERSHIP_PATH = {
  reassign_sales: '/deals/{dealId}/commands/reassign-sales',
  reassign_operations: '/deals/{dealId}/commands/reassign-operations',
  change_sales_support: '/deals/{dealId}/commands/change-sales-support',
} as const;

/** Ownership commands (reassign sales / operations owner, change sales support). If-Match; never optimistic. */
export function useOwnershipCommand(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ action, userId, reason }: { action: keyof typeof OWNERSHIP_PATH; userId: string | null; reason: string }) => {
      const params = { path: { dealId }, header: { ...ifMatch(await dealVersion(qc, dealId)), ...idempotent() } };
      if (action === 'change_sales_support') return unwrap(api.POST(OWNERSHIP_PATH.change_sales_support, { params, body: { userId, reason } }));
      if (!userId) throw new Error('A new owner is required.');
      return unwrap(api.POST(OWNERSHIP_PATH[action], { params, body: { userId, reason } }));
    },
    onSuccess: (deal) => setDeal(qc, deal),
    onError: () => void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId), exact: true }),
  });
}

export function useBulkReassign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'BulkDealRequest'>) => unwrap(api.POST('/deals/bulk', { body, params: { header: idempotent() } })),
    onSuccess: () => invalidateDealViews(qc),
  });
}

/** Asks the bank to list the customer's employer (Employers → enlistment requests); the form shows its status. */
export function useRequestEnlistment(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ employerId, bankId, note }: { employerId: string; bankId: string; note?: string }) => unwrap(api.POST('/employers/{employerId}/enlistments', { params: { path: { employerId }, header: idempotent() }, body: { bankId, ...(note ? { note } : {}) } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'form') });
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'overview') });
    },
  });
}

/** A4: recalculates on the server with overrides of editable inputs (nothing saved). Never optimistic. */
export function usePreviewCalculation(dealId: string) {
  return useMutation({
    mutationFn: (body: Schema<'DealCalculationRequest'>) => unwrap(api.POST('/deals/{dealId}/calculator/preview', { params: { path: { dealId } }, body })),
  });
}

/** A4: saves a calculation snapshot (server computes); refreshes the calculator, the history and the application. */
export function useSaveCalculation(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'DealCalculationRequest'>) => unwrap(api.POST('/deals/{dealId}/calculations', { params: { path: { dealId } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'calculator') });
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'calculations') });
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'application') });
    },
  });
}

// ---------------------------------------------------------------- A5: other documents, bank forms, generated application

/** Everything a requirement change shows in: the checklist, the bank forms, completeness and the deal's counts. */
function invalidateDocuments(qc: QueryClient, dealId: string) {
  void qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
  invalidateDealViews(qc);
}

/**
 * Adds an "Other document": a requirement of its own with the typed name (optional, owned by the deal). Its file is
 * then uploaded with useUploadDocument like every requirement (malware scan, versions, review). Never optimistic.
 */
export function useCreateOtherDocument(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => unwrap(api.POST('/deals/{dealId}/documents/custom', { params: { path: { dealId } }, body: { name } })),
    onSuccess: () => invalidateDocuments(qc, dealId),
  });
}

/** Removes an "Other document" that no version of was approved (its files stay for the audit trail). */
export function useRemoveOtherDocument(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => unwrap(api.DELETE('/deals/{dealId}/documents/{documentId}', { params: { path: { dealId, documentId } } })),
    onSuccess: () => invalidateDocuments(qc, dealId),
  });
}

/** Uploads the customer-signed copy of a bank form (upload intent of purpose document), with the date it was signed. */
export function useUploadSignedCopy(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ libraryFileId, file, issuedOn, onProgress }: { libraryFileId: string; file: File; issuedOn?: string; onProgress: (p: number) => void }) => {
      const uploadId = await uploadFile(file, onProgress);
      return unwrap(api.POST('/deals/{dealId}/bank-forms/{libraryFileId}/signed-copy', { params: { path: { dealId, libraryFileId } }, body: { uploadId, issuedOn: issuedOn ?? null } }));
    },
    onSuccess: () => invalidateDocuments(qc, dealId),
  });
}

/** Generates the bank application PDF on the server (masked or clear by the user's permission). */
export function useGenerateApplicationPdf(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/deals/{dealId}/application/generated', { params: { path: { dealId } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'generated') });
      void qc.invalidateQueries({ queryKey: dealKeys.tab(dealId, 'activity') });
    },
  });
}
